// NOTE: This is an adapdation of HighlightLayer.ts from Babylon
import { Vector2 as BABYLON_Vector2 } from '@inter-ikea-betrakta-babylonjs/core/Maths/math.vector.js';
import { Color3 as BABYLON_Color3, Color4 as BABYLON_Color4 } from '@inter-ikea-betrakta-babylonjs/core/Maths/math.color.js';
import { EffectFallbacks as BABYLON_EffectFallbacks } from '@inter-ikea-betrakta-babylonjs/core/Materials/effectFallbacks.js';
import { Material as BABYLON_Material } from '@inter-ikea-betrakta-babylonjs/core/Materials/material.js';
import { MaterialHelper as BABYLON_MaterialHelper } from '@inter-ikea-betrakta-babylonjs/core/Materials/materialHelper.js';
import { Constants as BABYLON_Constants } from '@inter-ikea-betrakta-babylonjs/core/Engines/constants.js';
import { Texture as BABYLON_Texture } from '@inter-ikea-betrakta-babylonjs/core/Materials/Textures/texture.js';
import { PostProcess as BABYLON_PostProcess } from '@inter-ikea-betrakta-babylonjs/core/PostProcesses/postProcess.js';
import { VertexBuffer as BABYLON_VertexBuffer } from '@inter-ikea-betrakta-babylonjs/core/Meshes/buffer.js';
// Make sure that the glowBlurPostProcess-shader we want is available
// TODO: Maybe make a copy of it, this feels a bit hacky. Or maybe there is a better way to force it in the ShadersStore?
import '@inter-ikea-betrakta-babylonjs/core/Shaders/glowBlurPostProcess.fragment.js';
// Side effects
import './shaders/outline_id_map_generation.vertex.js';
import './shaders/outline_id_map_generation.fragment.js';
import './shaders/outline_combine.vertex.js';
import './shaders/outline_combine.fragment.js';
import './shaders/outline_expand.fragment.js';
import { PartialViewportRenderTarget } from './utility/PartialViewportRenderTarget.js';
/*
  Mostly adapted from hilightLayer.ts
  Reuses shaders that writes solid color and blur, with custom merge (for now).

  TODO:
  * Glow is only active in a small part of the screen (usually). Processing in a smaller viewport could help on mobile.
  * Currently there can be gap between selected mesh and outline due to anti-aliasing. Fix!
*/
const DEFAULT_OUTLINE_COLOR = BABYLON_Color4.FromHexString('#1592E6');
export var OutlineState;
(function (OutlineState) {
    OutlineState[OutlineState["NONE"] = 0] = "NONE";
    OutlineState[OutlineState["OUTLINE"] = 1] = "OUTLINE";
    OutlineState[OutlineState["BLOCK"] = 2] = "BLOCK";
    OutlineState[OutlineState["OUTLINE_AND_BLOCK"] = 3] = "OUTLINE_AND_BLOCK";
})(OutlineState || (OutlineState = {}));
/* This pass is responsible for generating the data used for the outline combine pass.
 * Input: The outline render target, where the red channel contains the outline ID remapped to the 0-1 range.
 * Output: A texture where the red channel contains the dilated outline ID, green contains a binary mask, blue contains the outline width and alpha contains a blurred mask.
 */
class ExpandPostProcess extends BABYLON_PostProcess {
    constructor(name, kernel, direction, options, camera, sampling_mode = BABYLON_Texture.BILINEAR_SAMPLINGMODE, engine, reusable) {
        super(name, 'outline_expand', ['kernel', 'direction', 'uvScale'], ['textureSampler'], options, camera, sampling_mode, engine, reusable);
        this.kernel = kernel;
        this.direction = direction;
        this.onApplyObservable.add((effect) => {
            effect.setVector2('kernel', this.kernel);
            effect.setVector2('direction', this.direction);
        });
    }
}
export class OutlineLayer {
    get_effect_name() {
        return 'OutlineEffect';
    }
    constructor(name, scene) {
        this._vertexBuffers = {};
        this._cachedDefines = '';
        this._IDPassEffect = null; // Will be created in isReady func
        this._max_outline_colors = 10;
        this._blur_texture = null;
        this._post_processes = [];
        this._horizontal_blur_postprocess = null;
        this._vertical_blur_postprocess = null;
        this._clear_color = new BABYLON_Color4(0, 0, 0, 0);
        this._mainTextureDesiredSize = { width: 0, height: 0 };
        this._nr_of_objects_with_outline = 0;
        // TODO use weak maps instead? This needs to be done after or in conjunction with meterial-mesh rework.
        this._mesh_uid_to_outline_color = new Map();
        this._mesh_uid_to_outline_width = new Map();
        this._mesh_uid_to_outline_color_id = new Map();
        this._outline_colors = [];
        this._default_outline_color = DEFAULT_OUTLINE_COLOR;
        this._default_outline_width = 0.3;
        this._enabled = true;
        this._is_disposed = false;
        this._outlines_dirty = false;
        this._render_list = [];
        this.name = name;
        this._scene = scene;
        this._engine = this._scene.getEngine();
        this._mainTextureDesiredSize.width = this._engine.getRenderWidth();
        this._mainTextureDesiredSize.height = this._engine.getRenderHeight();
        this._mesh_outline_state = new Map();
        // Generate Buffers
        this._indexBuffer = this.generate_index_buffer();
        this._vertexBuffers[BABYLON_VertexBuffer.PositionKind] = this.generate_vertex_buffer();
        this._mainTexture = this.create_main_texture();
        this.create_texture_and_post_processes();
        this._merge_effect = this.create_merge_effect();
        this.rebuild_color_list();
    }
    /**
     * Generates the index buffer of the full screen quad blending to the main canvas.
     */
    generate_index_buffer() {
        const indices = [0, 1, 2, 0, 2, 3];
        return this._engine.createIndexBuffer(indices);
    }
    /**
     * Generates the vertex buffer of the full screen quad blending to the main canvas.
     */
    generate_vertex_buffer() {
        const vertices = [1, 1, -1, 1, -1, -1, 1, -1];
        return new BABYLON_VertexBuffer(this._engine, vertices, BABYLON_VertexBuffer.PositionKind, false, false, 2);
    }
    mark_outlines_dirty() {
        this._outlines_dirty = true;
    }
    clear_outlines_dirty() {
        this._outlines_dirty = false;
    }
    get_outlines_are_dirty() {
        return this._outlines_dirty;
    }
    should_render() {
        if (this.is_disposed) {
            console.error('Trying to call should_render on disposed outline layer');
            return false;
        }
        return this._enabled && this._nr_of_objects_with_outline > 0;
    }
    set enable(v) {
        if (this.is_disposed) {
            console.error('Trying to set enable on disposed outline layer');
            return;
        }
        this._enabled = v;
        this._mainTexture.should_render = this.should_render();
        this.mark_outlines_dirty();
    }
    get enable() {
        if (this.is_disposed) {
            console.error('Trying to get enable on disposed outline layer');
            return false;
        }
        return this._enabled;
    }
    /**
     * Creates the main texture for the effect layer.
     */
    create_main_texture() {
        const main_texture = new PartialViewportRenderTarget('HighlightLayerMainRTT', {
            width: this._scene.getEngine().getRenderWidth(),
            height: this._scene.getEngine().getRenderHeight()
        }, this._scene, false, true, 
        // INT and BYTE are equivalent here. But it makes much more sense to use BYTE here.
        BABYLON_Constants.TEXTURETYPE_UNSIGNED_BYTE, false, 
        // We enable bilinear filtering for efficient "blurring" in the combine shader.
        BABYLON_Constants.TEXTURE_BILINEAR_SAMPLINGMODE);
        main_texture.render_target.wrapU = BABYLON_Texture.CLAMP_ADDRESSMODE;
        main_texture.render_target.wrapV = BABYLON_Texture.CLAMP_ADDRESSMODE;
        main_texture.render_target.anisotropicFilteringLevel = 1;
        main_texture.render_target.updateSamplingMode(BABYLON_Texture.BILINEAR_SAMPLINGMODE);
        main_texture.render_target.renderParticles = false;
        main_texture.render_target.renderList = [];
        main_texture.render_target.ignoreCameraViewport = true; // NOTE: Even if we dont ignore camera Vieweport, we don't get frustum culling.
        // Custom render function
        main_texture.render_target.customRenderFunction = (opaque_sub_meshes, alpha_test_sub_meshes, transparent_sub_meshes, depth_only_sub_meshes) => {
            let index;
            const engine = this._engine;
            if (depth_only_sub_meshes.length) {
                engine.setColorWrite(false);
                for (index = 0; index < depth_only_sub_meshes.length; index++) {
                    this.render_sub_mesh(depth_only_sub_meshes.data[index]);
                }
                engine.setColorWrite(true);
            }
            for (index = 0; index < opaque_sub_meshes.length; index++) {
                this.render_sub_mesh(opaque_sub_meshes.data[index]);
            }
            for (index = 0; index < alpha_test_sub_meshes.length; index++) {
                this.render_sub_mesh(alpha_test_sub_meshes.data[index]);
            }
            const previous_alpha_mode = engine.getAlphaMode();
            for (index = 0; index < transparent_sub_meshes.length; index++) {
                this.render_sub_mesh(transparent_sub_meshes.data[index]);
            }
            engine.setAlphaMode(previous_alpha_mode);
            this._outlines_dirty = false;
        };
        main_texture.render_target.onClearObservable.add((engine) => {
            engine.clear(this._clear_color, true, true, true);
        });
        return main_texture;
    }
    render_sub_mesh(sub_mesh) {
        var _a, _b;
        const material = sub_mesh.getMaterial();
        const mesh = sub_mesh.getRenderingMesh();
        mesh._internalAbstractMeshDataInfo._isActiveIntermediate = false;
        if (!material) {
            return;
        }
        // Culling
        const reverse_side = mesh._getWorldMatrixDeterminant() < 0;
        this._engine.setState(material.backFaceCulling, 0, false, reverse_side);
        // Managing instances
        const batch = mesh._getInstancesRenderList(sub_mesh._id);
        if (batch.mustReturn) {
            return;
        }
        const hardware_instanced_rendering = batch.hardwareInstancedRendering[sub_mesh._id];
        if (this.is_ready(sub_mesh, hardware_instanced_rendering)) {
            if (this._IDPassEffect === null) {
                console.error('This should never happend. isReady should make sure IDPassEffect exists.');
                return;
            }
            this._engine.enableEffect(this._IDPassEffect);
            mesh._bind(sub_mesh, this._IDPassEffect, BABYLON_Material.TriangleFillMode);
            this._IDPassEffect.setMatrix('viewProjection', this._scene.getTransformMatrix());
            const max_colors = this._max_outline_colors;
            // If there are no instances, we simply update the outline color uniform.
            if (mesh.instances.length === 0) {
                const outline_state = this._mesh_outline_state.get(mesh.uniqueId);
                if (outline_state === undefined) {
                    return; // We should never get here, since we remove any meshes we dont want to render from the render list
                }
                const color_id = (_a = this._mesh_uid_to_outline_color_id.get(mesh.uniqueId)) !== null && _a !== void 0 ? _a : 0;
                let clip_mask = this.state_to_shader_value(outline_state);
                const color = this._mesh_uid_to_outline_color.get(mesh.uniqueId);
                if ((color === null || color === void 0 ? void 0 : color.a) === 0 && (outline_state === OutlineState.BLOCK || OutlineState.OUTLINE_AND_BLOCK)) {
                    clip_mask = 0; // Treat the opacity 0 case as an outline block
                }
                const width = (_b = this._mesh_uid_to_outline_width.get(mesh.uniqueId)) !== null && _b !== void 0 ? _b : this._default_outline_width;
                // Remap color_id to range from 0.09 to 1
                const color_id_value = remap(color_id + 1, 0, max_colors, 1 / (max_colors + 1), 1);
                this._IDPassEffect.setColor4('uOutlineColor', new BABYLON_Color3(color_id_value * clip_mask, clip_mask, width), clip_mask);
            }
            else {
                // If this (sub)mesh is instanced, we need to update the per-instance data buffers directly.
                // We also need to fetch the uniqueId differently, because otherwise we get the ID of the wrapping model (and not of each specific instance).
                mesh.instances.forEach(instanced_mesh => {
                    var _a, _b;
                    const color_id = (_a = this._mesh_uid_to_outline_color_id.get(instanced_mesh.uniqueId)) !== null && _a !== void 0 ? _a : 0;
                    const width = (_b = this._mesh_uid_to_outline_width.get(instanced_mesh.uniqueId)) !== null && _b !== void 0 ? _b : this._default_outline_width;
                    let clip_mask = 1;
                    const outline_state = this._mesh_outline_state.get(instanced_mesh.uniqueId);
                    if (outline_state) {
                        clip_mask = this.state_to_shader_value(outline_state);
                        if ((outline_state === OutlineState.BLOCK || OutlineState.OUTLINE_AND_BLOCK)) {
                            const color = this._mesh_uid_to_outline_color.get(instanced_mesh.uniqueId);
                            if (color) {
                                if (color.a === 0) {
                                    clip_mask = 0; // Treat the opacity 0 case as an outline block
                                }
                            }
                            else if (this._outline_colors[0].a === 0) {
                                clip_mask = 0; // Treat the opacity 0 case as an outline block
                            }
                        }
                    }
                    instanced_mesh.instancedBuffers[OutlineLayer.INSTANCE_DATA_ATTRIBUTE_NAME].x = clip_mask * remap(color_id + 1, 0, max_colors, 1 / (max_colors + 1), 1);
                    instanced_mesh.instancedBuffers[OutlineLayer.INSTANCE_DATA_ATTRIBUTE_NAME].y = clip_mask; // We write the original mask to the y channel, to have a binary inside/outside value
                    instanced_mesh.instancedBuffers[OutlineLayer.INSTANCE_DATA_ATTRIBUTE_NAME].z = width;
                    instanced_mesh.instancedBuffers[OutlineLayer.INSTANCE_DATA_ATTRIBUTE_NAME].w = clip_mask; // The alpha component also starts out with a binary mask, but this gets blurred
                });
            }
            // Bones
            if (mesh.useBones && mesh.computeBonesUsingShaders && mesh.skeleton) {
                const skeleton = mesh.skeleton;
                if (skeleton.isUsingTextureForMatrices) {
                    const bone_texture = skeleton.getTransformMatrixTexture(mesh);
                    if (!bone_texture) {
                        return;
                    }
                    this._IDPassEffect.setTexture('boneSampler', bone_texture);
                    this._IDPassEffect.setFloat('boneTextureWidth', 4.0 * (skeleton.bones.length + 1));
                }
                else {
                    this._IDPassEffect.setMatrices('mBones', skeleton.getTransformMatrices((mesh)));
                }
            }
            // Morph targets
            BABYLON_MaterialHelper.BindMorphTargetParameters(mesh, this._IDPassEffect);
            // Draw
            mesh._processRendering(mesh, sub_mesh, this._IDPassEffect, material.fillMode, batch, hardware_instanced_rendering, (is_instance, world) => {
                this._IDPassEffect.setMatrix('world', world);
            });
        }
        else {
            // Need to reset refresh rate of the main map
            this._mainTexture.render_target.resetRefreshCounter();
        }
    }
    create_merge_effect() {
        // Effect
        return this._engine.createEffect('outline_combine', [BABYLON_VertexBuffer.PositionKind], ['colors', 'halfSourcePixelSize', 'uvScale'], ['textureSampler', 'unblurredTextureSampler'], undefined);
    }
    create_texture_and_post_processes() {
        this._blur_texture = new PartialViewportRenderTarget('HighlightLayerBlurRTT', { width: this._mainTextureDesiredSize.width, height: this._mainTextureDesiredSize.height }, this._scene, false, true, BABYLON_Constants.TEXTURETYPE_UNSIGNED_INT);
        const bt = this._blur_texture.render_target;
        bt.wrapU = BABYLON_Texture.CLAMP_ADDRESSMODE;
        bt.wrapV = BABYLON_Texture.CLAMP_ADDRESSMODE;
        bt.renderParticles = false;
        bt.ignoreCameraViewport = true;
        // The pixel distance between the samples. Non-integer numbers seem to cause strange banding issues
        const size = 2;
        this._horizontal_blur_postprocess = new ExpandPostProcess('Expand Horizontal', new BABYLON_Vector2(), new BABYLON_Vector2(1, 0), 1, null, BABYLON_Texture.BILINEAR_SAMPLINGMODE, this._scene.getEngine());
        this._horizontal_blur_postprocess.onApplyObservable.add((effect) => {
            effect.setFloat2('kernel', size / this._mainTexture.render_target.getSize().width, size / this._mainTexture.render_target.getSize().height);
            effect.setFloat2('direction', 1, 0);
            effect.setTexture('textureSampler', this._mainTexture.render_target);
            effect.setVector2('uvScale', this._mainTexture.get_full_to_partial_scale());
        });
        this._vertical_blur_postprocess = new ExpandPostProcess('Expand Vertical', new BABYLON_Vector2(), new BABYLON_Vector2(0, 1), 1, null, BABYLON_Texture.BILINEAR_SAMPLINGMODE, this._scene.getEngine());
        this._vertical_blur_postprocess.onApplyObservable.add((effect) => {
            effect.setFloat2('kernel', size / this._mainTexture.render_target.getSize().width, size / this._mainTexture.render_target.getSize().height);
            effect.setFloat2('direction', 0, 1);
            effect.setTexture('textureSampler', this._blur_texture.render_target);
            effect.setVector2('uvScale', this._mainTexture.get_full_to_partial_scale());
        });
        this._post_processes = [this._horizontal_blur_postprocess, this._vertical_blur_postprocess];
        // Register Blur effect after main texture is rendered
        this._mainTexture.render_target.onAfterUnbindObservable.add(() => {
            const internal_texture = bt.renderTarget;
            if (internal_texture) {
                this._scene.postProcessManager.directRender([this._horizontal_blur_postprocess], bt.renderTarget, true);
                this._scene.postProcessManager.directRender([this._vertical_blur_postprocess], this._mainTexture.render_target.renderTarget, true);
            }
        });
        // Prevent autoClear.
        // Could this use forEach instead?
        // eslint-disable-next-line array-callback-return
        this._post_processes.map((pp) => { pp.autoClear = false; });
    }
    internal_render(effect) {
        // Texture
        effect.setTexture('textureSampler', this._mainTexture.render_target || null);
        // Cache
        const engine = this._engine;
        engine.drawElementsType(BABYLON_Material.TriangleFillMode, 0, 6);
    }
    render_outline_ontop_of_main(camera, target, width, height, offset_x, offset_y) {
        var _a;
        if (!this.should_render()) {
            return;
        }
        // Check
        if (!this._merge_effect.isReady()) {
            return;
        }
        for (let i = 0; i < this._post_processes.length; i++) {
            if (!this._post_processes[i].isReady()) {
                return;
            }
        }
        (_a = this._blur_texture) === null || _a === void 0 ? void 0 : _a.get_temporary_partial_render_target(width, height);
        this._mainTexture.get_temporary_partial_render_target(width, height);
        this._mainTexture.render_target.activeCamera = camera;
        // For some reason, unless we specify this flag to true, it skips the layer mask check when rendering.
        this._mainTexture.render_target.forceLayerMaskCheck = true;
        // If the outline alpha is 0 we dont want to render the mesh, unless we also told the object to block outlines.
        // That case is handled further in the render_sub_mesh method of this class.
        this._mainTexture.render_target.renderList = this._render_list.filter((mesh) => {
            const color = this._mesh_uid_to_outline_color.get(mesh.uniqueId);
            const state = this.get_outline_state(mesh);
            if (state !== OutlineState.BLOCK && state !== OutlineState.OUTLINE_AND_BLOCK) {
                return color ? color.a > 0 : this._outline_colors[0].a > 0; // If we dont have a color stored for the mesh, it means we check the default outline color at index 0.
            }
            else {
                return true;
            }
        });
        // Since we're calling a Babylon render function, we should increment the render id. Yes this is silly.
        this._scene.incrementRenderId();
        this._mainTexture.render_target.render(false, false);
        const engine = this._scene.getEngine();
        // Render
        engine.enableEffect(this._merge_effect);
        engine.setState(false);
        const flattened_colors = [];
        for (const color of this._outline_colors) {
            flattened_colors.push(color.r);
            flattened_colors.push(color.g);
            flattened_colors.push(color.b);
            flattened_colors.push(color.a);
        }
        this._merge_effect.setFloatArray4('colors', flattened_colors);
        this._merge_effect.setVector2('uvScale', this._mainTexture.get_full_to_partial_scale());
        if (this._blur_texture) {
            const actual_size = this._blur_texture.get_actual_size();
            this._merge_effect.setFloat2('halfSourcePixelSize', 0.5 / actual_size.x, 0.5 / actual_size.y);
        }
        // Cache
        const previous_alpha_mode = engine.getAlphaMode();
        // Go Blend.
        engine.setAlphaMode(BABYLON_Constants.ALPHA_COMBINE);
        if (target === null) {
            engine.restoreDefaultFramebuffer();
        }
        else {
            target._bindFrameBuffer();
        }
        // VBOs
        engine.bindBuffers(this._vertexBuffers, this._indexBuffer, this._merge_effect);
        this._engine._gl.viewport(offset_x, offset_y, width, height);
        // Blends the map on the main canvas.
        this.internal_render(this._merge_effect);
        // Restore Alpha
        engine.setAlphaMode(previous_alpha_mode);
    }
    /**
       * Checks for the readiness of the element composing the layer.
       * @param sub_mesh the mesh to check for
       * @param use_instances specify whether or not to use instances to render the mesh
       * @return true if ready otherwise, false
       */
    is_ready(sub_mesh, use_instances) {
        const material = sub_mesh.getMaterial();
        if (!material) {
            return false;
        }
        if (!material.isReadyForSubMesh(sub_mesh.getMesh(), sub_mesh, use_instances)) {
            return false;
        }
        const defines = [];
        const attribs = [BABYLON_VertexBuffer.PositionKind];
        const mesh = sub_mesh.getMesh();
        // Bones
        const fallbacks = new BABYLON_EffectFallbacks();
        if (mesh.useBones && mesh.computeBonesUsingShaders) {
            attribs.push(BABYLON_VertexBuffer.MatricesIndicesKind);
            attribs.push(BABYLON_VertexBuffer.MatricesWeightsKind);
            if (mesh.numBoneInfluencers > 4) {
                attribs.push(BABYLON_VertexBuffer.MatricesIndicesExtraKind);
                attribs.push(BABYLON_VertexBuffer.MatricesWeightsExtraKind);
            }
            defines.push('#define NUM_BONE_INFLUENCERS ' + mesh.numBoneInfluencers);
            const skeleton = mesh.skeleton;
            if (skeleton && skeleton.isUsingTextureForMatrices) {
                defines.push('#define BONETEXTURE');
            }
            else {
                defines.push('#define BonesPerMesh ' + (skeleton ? (skeleton.bones.length + 1) : 0));
            }
            if (mesh.numBoneInfluencers > 0) {
                fallbacks.addCPUSkinningFallback(0, mesh);
            }
        }
        else {
            defines.push('#define NUM_BONE_INFLUENCERS 0');
        }
        // Morph targets
        const manager = mesh.morphTargetManager;
        let morph_influencers = 0;
        if (manager) {
            if (manager.numInfluencers > 0) {
                defines.push('#define MORPHTARGETS');
                morph_influencers = manager.numInfluencers;
                defines.push('#define NUM_MORPH_INFLUENCERS ' + morph_influencers);
                BABYLON_MaterialHelper.PrepareAttributesForMorphTargetsInfluencers(attribs, mesh, morph_influencers);
            }
        }
        // Instances
        if (use_instances) {
            defines.push('#define INSTANCES');
            BABYLON_MaterialHelper.PushAttributesForInstances(attribs);
            attribs.push(OutlineLayer.INSTANCE_DATA_ATTRIBUTE_NAME);
        }
        // Get correct effect
        const join = defines.join('\n');
        if (this._cachedDefines !== join || this._IDPassEffect === null) {
            this._cachedDefines = join;
            this._IDPassEffect = this._scene.getEngine().createEffect('outline_id_map_generation', attribs, ['world', 'mBones', 'viewProjection', 'morphTargetInfluences', 'boneTextureWidth', 'uOutlineColor'], ['boneSampler'], join, fallbacks, undefined, undefined, { maxSimultaneousMorphTargets: morph_influencers });
        }
        return this._IDPassEffect.isReady();
    }
    get is_disposed() {
        return this._is_disposed;
    }
    dispose() {
        if (!this.is_disposed) {
            this._render_list.length = 0;
            this._dispose_texture_and_post_processes();
            this._is_disposed = true;
        }
    }
    _dispose_texture_and_post_processes() {
        // Remove mainTexture from scenes rendertargets
        var _a;
        this._mainTexture.dispose();
        // Could this use forEach instead?
        this._post_processes.map(pp => pp.dispose());
        this._post_processes = [];
        (_a = this._blur_texture) === null || _a === void 0 ? void 0 : _a.dispose();
    }
    add_to_rendertarget(mesh) {
        const i = this._render_list.indexOf(mesh);
        if (i < 0) {
            this._render_list.push(mesh);
        }
    }
    remove_from_rendertarget(mesh) {
        const i = this._render_list.indexOf(mesh);
        if (i > -1) {
            this._render_list.splice(i, 1);
        }
        this.mark_outlines_dirty();
    }
    set_outline_state(mesh, state) {
        if (state === OutlineState.NONE) {
            this.remove_mesh(mesh);
        }
        else {
            this._mesh_outline_state.set(mesh.uniqueId, state);
            this.add_to_rendertarget(mesh);
        }
        this.mark_outlines_dirty();
    }
    // Send state to shader
    state_to_shader_value(state) {
        switch (state) {
            case OutlineState.OUTLINE:
            case OutlineState.OUTLINE_AND_BLOCK:
                return 1;
            case OutlineState.BLOCK:
                return 0;
            case OutlineState.NONE:
                return -1; // None is dont care, it should not be renderered. Nothing should depend on this, since we use a render list to keep track on what to render.
            default:
                throw new Error('OutlineEffect - Invalid OutlineState');
        }
    }
    get_outline_state(mesh) {
        const state_value = this._mesh_outline_state.get(mesh.uniqueId);
        return state_value === undefined ? OutlineState.NONE : state_value;
    }
    /**
     * Set if a SceneObject should block other object's outlines.
     * @param object SceneObject reference.
     * @param enabled
     */
    set_outline_block(object, enabled) {
        if (this.is_disposed) {
            console.error('Trying to call set_outline_block on disposed outline layer');
            return;
        }
        object.mesh_iterator(false, mesh => {
            const current = this.get_outline_state(mesh);
            if (!enabled) {
                if (current === OutlineState.BLOCK) {
                    this.remove_mesh(mesh);
                }
                if (current === OutlineState.OUTLINE_AND_BLOCK) {
                    this.set_outline_state(mesh, OutlineState.OUTLINE);
                }
            }
            else {
                if (current === OutlineState.OUTLINE) {
                    this.set_outline_state(mesh, OutlineState.OUTLINE_AND_BLOCK);
                }
                if (current === OutlineState.NONE) {
                    this.set_outline_state(mesh, OutlineState.BLOCK);
                }
            }
        });
    }
    /**
     * Set if a SceneObject should have outline enabled.
     * @param object SceneObject reference.
     * @param enabled
     */
    set_outline(object, enabled) {
        if (this.is_disposed) {
            console.error('Trying to call set_outline on disposed outline layer');
            return;
        }
        object.mesh_iterator(false, mesh => {
            const current = this.get_outline_state(mesh);
            if (!enabled) {
                if (current === OutlineState.OUTLINE) {
                    this.remove_mesh(mesh); // remove_mesh will decrease nr_of_objects_with_outline if needed
                }
                if (current === OutlineState.OUTLINE_AND_BLOCK) {
                    this.set_outline_state(mesh, OutlineState.BLOCK);
                    this._nr_of_objects_with_outline--;
                    this._mainTexture.should_render = this.should_render();
                }
            }
            else {
                if (current === OutlineState.BLOCK) {
                    this.set_outline_state(mesh, OutlineState.OUTLINE_AND_BLOCK);
                    this._nr_of_objects_with_outline++;
                    this._mainTexture.should_render = this.should_render();
                }
                if (current === OutlineState.NONE) {
                    this.set_outline_state(mesh, OutlineState.OUTLINE);
                    this._nr_of_objects_with_outline++;
                    this._mainTexture.should_render = this.should_render();
                }
            }
        });
        this.rebuild_color_list();
    }
    remove_mesh(mesh) {
        if (this.is_disposed) {
            console.error('Trying to call remove_mesh on disposed outline layer');
            return;
        }
        const state = this.get_outline_state(mesh);
        if (state === OutlineState.NONE) {
            return;
        } // Mesh is not part of effect
        this._mesh_uid_to_outline_color.delete(mesh.uniqueId);
        this._mesh_uid_to_outline_width.delete(mesh.uniqueId);
        // Check if number of objects with outline needs to be updated
        if (state === OutlineState.OUTLINE || state === OutlineState.OUTLINE_AND_BLOCK) {
            this._nr_of_objects_with_outline--;
            this._mainTexture.should_render = this.should_render();
        }
        this._mesh_outline_state.delete(mesh.uniqueId);
        this.remove_from_rendertarget(mesh);
        // We could also clean up instancebuffer value here, however, it does not matter for this effect since it will be overwritten if outline would be enabled again.
    }
    set_global_outline_color(color) {
        if (this.is_disposed) {
            console.error('Trying to call set_global_outline_color on disposed outline layer');
            return;
        }
        if (color === undefined) {
            color = DEFAULT_OUTLINE_COLOR;
        }
        this._default_outline_color = color;
        this.rebuild_color_list();
        this.mark_outlines_dirty();
    }
    set_global_outline_width(width) {
        this._default_outline_width = width;
        this.mark_outlines_dirty();
    }
    get_global_outline_width() {
        return this._default_outline_width;
    }
    set_object_outline_width(object, width) {
        object.mesh_iterator(false, mesh => {
            if (width !== null) {
                this._mesh_uid_to_outline_width.set(mesh.uniqueId, width);
            }
            else {
                this._mesh_uid_to_outline_width.delete(mesh.uniqueId);
            }
        });
        this.rebuild_color_list();
        this.mark_outlines_dirty();
    }
    get_global_outline_color() {
        if (this.is_disposed) {
            console.error('Trying to call get_global_outline_color on disposed outline layer');
            return new BABYLON_Color4(Number.NaN, Number.NaN, Number.NaN, Number.NaN);
        }
        return this._default_outline_color;
    }
    set_object_outline_color(object, color) {
        if (this.is_disposed) {
            console.error('Trying to call set_object_outline_color on disposed outline layer');
            return;
        }
        object.mesh_iterator(false, mesh => {
            if (color) {
                this._mesh_uid_to_outline_color.set(mesh.uniqueId, color);
            }
            else {
                this._mesh_uid_to_outline_color.delete(mesh.uniqueId);
            }
        });
        this.rebuild_color_list();
        this.mark_outlines_dirty();
    }
    rebuild_color_list() {
        var _a;
        this._outline_colors.length = 0;
        this._mesh_uid_to_outline_color_id.clear();
        // Set default color to index '0'
        this._outline_colors.push(this._default_outline_color);
        for (const mesh of (_a = this._render_list) !== null && _a !== void 0 ? _a : []) {
            // Check if we have stored outline info
            const color = this._mesh_uid_to_outline_color.get(mesh.uniqueId);
            if (color) {
                const outline_color = color !== null && color !== void 0 ? color : this._default_outline_color;
                // Find closest color4 stored
                let index = this._outline_colors.findIndex((color) => (Math.abs(color.r - outline_color.r) < 0.001 &&
                    Math.abs(color.g - outline_color.g) < 0.001 &&
                    Math.abs(color.b - outline_color.b) < 0.001 &&
                    Math.abs(color.a - outline_color.a) < 0.001));
                if (index < 0) {
                    // Add new color if not found
                    index = this._outline_colors.push(outline_color) - 1;
                }
                // Store the index for this mesh
                this._mesh_uid_to_outline_color_id.set(mesh.uniqueId, index);
            }
        }
    }
}
/**
 * The name of the instancing attribute we use to pass outline data to the shader.
 * If the name is changed here, it must be updated in the outline vertex shader as well.
 */
OutlineLayer.INSTANCE_DATA_ATTRIBUTE_NAME = 'outline_instance_data';
OutlineLayer.INSTANCE_DATA_ATTRIBUTE_STRIDE = 4;
function remap(value, old_min, old_max, new_min, new_max) {
    return (value - old_min) / (old_max - old_min) * (new_max - new_min) + new_min;
}
//# sourceMappingURL=OutlineEffect.js.map